/* ============================================================
   Native Possum — np-header.js
   Everything the header (snippets/np-header.liquid) does, at every width.
     LOOK      .is-solid — phone/tablet: scrolled past 50px or something open
               from the header; desktop: hovered / keyboard used / search open /
               compact. Only matters on the homepage (.np-hdr--transparent).
     COMPACT   ≥1201: .is-compact once the nav row scrolls off the top. The slot
               (#npHeader) keeps the header's height so the page doesn't jump.
     SEARCH    one panel (#npSearch) for every width; an npLayer in group
               'header' with the menu and the bag, so only one is ever open.
     CURRENCY  desktop <select>; tablet globe + list (an npLayer). The select is
               the form's one country_code field — the list writes into it.
     NAV       desktop dropdowns: hover opens; Enter follows the link (a link
               with no page, href "#", opens instead); Space or ↓ opens and
               moves into it; Escape closes (a hover-opened one too, wherever
               focus is). Touch: first tap opens, second
               follows the link.
     BAG       the bag button opens/closes window.bagDrawer.
     --np-header-h  height of the fixed phone/tablet header (body padding,
               menu, bag and product page offsets).
   .is-search-open / .is-menu-open / .is-bag-open follow npLayer's 'np:layer'
   event, so the menu and the bag never reach into the header.
   Depends on: np-utilities.js (npLayer, npSwitchCurrency, npIsSearchPage),
   np-predictive.js (npPredictiveSearch, dropdown contents).
   ============================================================ */

(function () {
  'use strict';

  var slot = document.getElementById('npHeader');
  var hdr = slot && slot.querySelector('[data-np-header]');
  if (!hdr) return;

  var mqPhone = window.matchMedia('(max-width: 767px)');
  var mqDesktop = window.matchMedia('(min-width: 991px)');
  var mqCompact = window.matchMedia('(min-width: 1201px)');

  var open = { search: false, menu: false, bag: false };
  var hovered = false;
  var scrolled = false;

  // ============================================
  // LOOK
  // ============================================
  function updateLook() {
    var solid = mqDesktop.matches
      ? hovered || open.search || hdr.classList.contains('is-compact')
      : scrolled || open.search || open.menu || open.bag;
    hdr.classList.toggle('is-solid', solid);
    hdr.classList.toggle('is-search-open', open.search);
    hdr.classList.toggle('is-menu-open', open.menu);
    hdr.classList.toggle('is-bag-open', open.bag);
  }

  hdr.addEventListener('mouseenter', function () {
    if (!mqDesktop.matches) return;
    hovered = true;
    updateLook();
  });

  hdr.addEventListener('mouseleave', function () {
    if (!mqDesktop.matches || open.search) return;
    hovered = false;
    updateLook();
  });

  // Keyboard users: the homepage header turns solid so the nav is readable
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || hovered) return;
    hovered = true;
    updateLook();
  });

  // ============================================
  // COMPACT BAR (≥1201) + HEADER HEIGHT
  // ============================================
  var nav = hdr.querySelector('.np-hdr__nav');
  var navOffset = 0; // nav row's distance from the top of the slot, measured while not compact

  function measure() {
    if (!hdr.classList.contains('is-compact')) {
      navOffset = nav.getBoundingClientRect().top - slot.getBoundingClientRect().top;
    }
    if (!mqDesktop.matches) {
      document.documentElement.style.setProperty('--np-header-h', hdr.offsetHeight + 'px');
    }
  }

  function updateCompact() {
    var want = mqCompact.matches && slot.getBoundingClientRect().top + navOffset <= 0;
    if (want === hdr.classList.contains('is-compact')) return;
    slot.style.height = want ? slot.offsetHeight + 'px' : '';
    hdr.classList.toggle('is-compact', want);
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      scrolled = window.scrollY > 50;
      updateCompact();
      updateLook();
    });
  }

  function onWidthChange() {
    if (!mqCompact.matches && hdr.classList.contains('is-compact')) {
      hdr.classList.remove('is-compact');
      slot.style.height = '';
    }
    measure();
    updateCompact();
    updateLook();
  }

  new ResizeObserver(measure).observe(hdr);
  window.addEventListener('scroll', onScroll, { passive: true });
  [mqPhone, mqDesktop, mqCompact].forEach(function (mq) {
    mq.addEventListener('change', onWidthChange);
  });

  // ============================================
  // OPEN STATES (from npLayer)
  // ============================================
  var bagBtn = hdr.querySelector('.np-hdr__bag-btn');
  var bagBadge = hdr.querySelector('[data-np-bag-count]');

  function updateBagLabel() {
    if (!bagBtn) return;
    if (open.bag && mqPhone.matches) {
      bagBtn.setAttribute('aria-label', 'Close bag');
      return;
    }
    var count = bagBadge ? parseInt(bagBadge.textContent, 10) || 0 : 0;
    bagBtn.setAttribute('aria-label', 'Shopping bag' + (count > 0 ? ', ' + count + ' item' + (count !== 1 ? 's' : '') : ''));
  }

  document.addEventListener('np:layer', function (e) {
    var id = e.detail && e.detail.panel && e.detail.panel.id;
    if (id === 'npSearch') {
      open.search = e.detail.open;
      if (!open.search && !hdr.matches(':hover')) hovered = false;
    } else if (id === 'npMobileMenu') {
      open.menu = e.detail.open;
    } else if (id === 'bagDrawer') {
      open.bag = e.detail.open;
    } else {
      return;
    }
    updateLook();
    updateBagLabel();
  });

  // The bag drawer writes the new count into the badge in its own listener.
  document.addEventListener('np:cart:updated', function () {
    Promise.resolve().then(updateBagLabel);
  });

  // ============================================
  // BAG BUTTON
  // ============================================
  if (bagBtn) {
    bagBtn.addEventListener('click', function (e) {
      e.preventDefault();
      var bag = window.bagDrawer;
      if (!bag) return;
      if (bag.isOpen()) {
        bag.close();
      } else {
        bag.open(bagBtn);
        bag.updateBag();
      }
    });
  }

  // ============================================
  // SEARCH
  // ============================================
  var searchBtn = hdr.querySelector('.np-hdr__search-btn');
  var panel = document.getElementById('npSearch');
  var input = document.getElementById('npSearchInput');
  var results = document.getElementById('npSearchResults');
  var backdrop = null;
  var debounce = null;

  function clearResults() {
    clearTimeout(debounce);
    results.classList.remove('active');
    results.innerHTML = '';
  }

  function onSearchPage() {
    return window.npIsSearchPage() && new URLSearchParams(window.location.search).get('q');
  }

  function openSearch() {
    if (window.npLayer.isOpen(panel)) return;
    panel.classList.add('is-open');
    var dim = null;
    if (!mqPhone.matches) {
      if (!backdrop) {
        backdrop = document.createElement('div');
        backdrop.className = 'np-search-backdrop';
        backdrop.setAttribute('aria-hidden', 'true');
        document.body.appendChild(backdrop);
      }
      backdrop.offsetHeight; // start the fade from transparent
      backdrop.classList.add('active');
      dim = backdrop;
    }
    window.npLayer.open(panel, {
      trigger: searchBtn,
      keep: [hdr],
      backdrop: dim,
      closeOnKeepUse: true,
      group: 'header',
      initialFocus: input,
      onRequestClose: closeSearch
    });
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { input.select(); });
    });
  }

  function closeSearch() {
    if (!window.npLayer.isOpen(panel)) return;
    panel.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('active');
    if (!onSearchPage()) input.value = '';
    clearResults();
    window.npLayer.close(panel);
  }

  if (searchBtn && panel && input && results) {
    searchBtn.addEventListener('click', function () {
      if (window.npLayer.isOpen(panel)) closeSearch();
      else openSearch();
    });

    panel.querySelector('.np-hdr__search-close').addEventListener('click', closeSearch);

    input.addEventListener('input', function () {
      var query = input.value.trim();
      clearTimeout(debounce);
      if (query.length < 2) {
        clearResults();
        return;
      }
      debounce = setTimeout(function () {
        if (window.npPredictiveSearch) {
          window.npPredictiveSearch.fetch(query, results, { mobile: mqPhone.matches });
        }
      }, 300);
    });

    // Phone: scrolling the results puts the keyboard away
    results.addEventListener('scroll', function () {
      if (mqPhone.matches && document.activeElement === input) input.blur();
    }, { passive: true });

    // Results page: keep the shopper's words in the box
    if (onSearchPage()) input.value = new URLSearchParams(window.location.search).get('q');
  }

  // ============================================
  // CURRENCY
  // ============================================
  var form = document.getElementById('npHeaderCurrencyForm');
  if (form) {
    var select = form.querySelector('[data-np-currency-select]');
    var globe = form.querySelector('.np-hdr__globe-btn');
    var list = form.querySelector('.np-hdr__currency-list');
    var options = Array.prototype.slice.call(list.querySelectorAll('[role="option"]'));

    var choose = function (code) {
      select.value = code;
      window.npSwitchCurrency(code, form);
    };

    select.addEventListener('change', function () { choose(select.value); });

    var closeList = function () {
      list.classList.remove('is-open');
      window.npLayer.close(list);
    };

    var openList = function () {
      list.classList.add('is-open');
      window.npLayer.open(list, {
        trigger: globe,
        keep: [globe],
        initialFocus: list.querySelector('.is-selected'),
        onRequestClose: closeList
      });
    };

    globe.addEventListener('click', function (e) {
      e.preventDefault();
      if (window.npLayer.isOpen(list)) closeList();
      else openList();
    });

    options.forEach(function (option) {
      option.addEventListener('click', function () { choose(option.getAttribute('data-country')); });
    });

    list.addEventListener('keydown', function (e) {
      var i = options.indexOf(document.activeElement);
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        var step = e.key === 'ArrowDown' ? 1 : -1;
        options[(i + step + options.length) % options.length].focus();
      } else if (e.key === 'Tab') {
        closeList();
      }
    });

    document.addEventListener('click', function (e) {
      if (window.npLayer.isOpen(list) && !globe.contains(e.target) && !list.contains(e.target)) closeList();
    });
  }

  // ============================================
  // NAV DROPDOWNS (desktop)
  // ============================================
  var lastPointer = 'mouse';
  document.addEventListener('pointerdown', function (e) { lastPointer = e.pointerType || 'mouse'; }, true);

  var dropItems = Array.prototype.filter.call(hdr.querySelectorAll('.np-nav__item'), function (item) {
    return item.querySelector('.np-dropdown');
  });

  function setDrop(item, on) {
    item.querySelector('.np-nav__link').setAttribute('aria-expanded', on ? 'true' : 'false');
    if (on) item.classList.remove('is-dismissed');
  }

  function closeDrops(except) {
    dropItems.forEach(function (item) { if (item !== except) setDrop(item, false); });
  }

  dropItems.forEach(function (item) {
    var link = item.querySelector('.np-nav__link');
    var drop = item.querySelector('.np-dropdown');
    var href = link.getAttribute('href');
    var hasPage = href && href !== '#';

    item.addEventListener('mouseenter', function () {
      if (lastPointer !== 'touch') setDrop(item, true);
    });

    item.addEventListener('mouseleave', function () {
      if (lastPointer !== 'touch') setDrop(item, false);
      item.classList.remove('is-dismissed');
    });

    link.addEventListener('click', function (e) {
      var isOpen = link.getAttribute('aria-expanded') === 'true';
      if (!hasPage) {
        e.preventDefault();
        closeDrops(item);
        setDrop(item, !isOpen);
        return;
      }
      // Touch: first tap opens, second follows the link. Keyboard (detail 0) and mouse follow it.
      if (e.detail > 0 && lastPointer === 'touch' && !isOpen) {
        e.preventDefault();
        closeDrops(item);
        setDrop(item, true);
      }
    });

    link.addEventListener('keydown', function (e) {
      if (e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        closeDrops(item);
        setDrop(item, true);
        var first = drop.querySelector('a');
        if (first) first.focus();
      }
    });

    // Escape: close and stay on the top link (is-dismissed beats :focus-within)
    item.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      setDrop(item, false);
      item.classList.add('is-dismissed');
      link.focus();
    });

    item.addEventListener('focusin', function (e) {
      if (!item.contains(e.relatedTarget)) item.classList.remove('is-dismissed');
    });

    item.addEventListener('focusout', function (e) {
      if (!item.contains(e.relatedTarget)) {
        setDrop(item, false);
        item.classList.remove('is-dismissed');
      }
    });
  });

  // Escape also closes a dropdown opened by hovering, wherever focus is
  // (WCAG 1.4.13 — hover content must be dismissible without moving the
  // pointer). Focus inside an item is handled by that item's own Escape.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    dropItems.forEach(function (item) {
      if (item.contains(document.activeElement)) return;
      if (item.matches(':hover') || item.querySelector('.np-nav__link').getAttribute('aria-expanded') === 'true') {
        setDrop(item, false);
        item.classList.add('is-dismissed');
      }
    });
  });

  // Touch: a tap anywhere else closes an open dropdown
  document.addEventListener('click', function (e) {
    if (lastPointer !== 'touch') return;
    dropItems.forEach(function (item) {
      if (!item.contains(e.target)) setDrop(item, false);
    });
  });

  // ============================================
  // START
  // ============================================
  // Layers opened before this deferred file ran (the bag reopens itself
  // after a currency switch) didn't reach the 'np:layer' listener.
  ['npMobileMenu', 'bagDrawer'].forEach(function (id) {
    var el = document.getElementById(id);
    if (el && window.npLayer.isOpen(el)) open[id === 'bagDrawer' ? 'bag' : 'menu'] = true;
  });

  scrolled = window.scrollY > 50;
  measure();
  updateCompact();
  updateLook();
  updateBagLabel();
})();
